import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Code, Database, Cloud, Globe, Server, Cpu, Layers, Smartphone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface CompanyTechStackTabProps {
  companyId: string;
  companyName: string;
}

interface TechItem {
  id: string;
  technology_name: string;
  category: string | null;
  confidence_score: number | null;
  source: string | null;
  discovered_at: string;
}

const categoryConfig: Record<string, { icon: any; color: string; label: string }> = {
  frontend: { icon: Globe, color: 'bg-blue-500/10 text-blue-600', label: 'Frontend' },
  backend: { icon: Server, color: 'bg-green-500/10 text-green-600', label: 'Backend' },
  database: { icon: Database, color: 'bg-purple-500/10 text-purple-600', label: 'Database' },
  cloud: { icon: Cloud, color: 'bg-cyan-500/10 text-cyan-600', label: 'Cloud' },
  devops: { icon: Layers, color: 'bg-orange-500/10 text-orange-600', label: 'DevOps' },
  mobile: { icon: Smartphone, color: 'bg-pink-500/10 text-pink-600', label: 'Mobile' },
  ai_ml: { icon: Cpu, color: 'bg-indigo-500/10 text-indigo-600', label: 'AI/ML' },
  language: { icon: Code, color: 'bg-amber-500/10 text-amber-600', label: 'Language' },
};

export function CompanyTechStackTab({ companyId, companyName }: CompanyTechStackTabProps) {
  const [techStack, setTechStack] = useState<TechItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTechStack = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('company_tech_stack')
        .select('*')
        .eq('company_id', companyId)
        .order('category', { ascending: true });

      if (error) throw error;
      setTechStack(data || []);
    } catch (error) {
      console.error('Error fetching tech stack:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTechStack();
  }, [companyId]);

  const getCategoryConfig = (category: string | null) => {
    return categoryConfig[category || ''] || { icon: Code, color: 'bg-gray-500/10 text-gray-600', label: category || 'Other' };
  };

  // Group by category
  const groupedTech = techStack.reduce((acc, tech) => {
    const cat = tech.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tech);
    return acc;
  }, {} as Record<string, TechItem[]>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Technology Stack</h3>
          <p className="text-sm text-muted-foreground">
            {techStack.length} technologies identified
          </p>
        </div>
      </div>

      {techStack.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Code className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h4 className="font-medium text-muted-foreground mb-2">No tech stack data yet</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Use "AI Enrich" to discover technologies used by {companyName}
            </p>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[500px]">
          <div className="space-y-6 pr-4">
            {Object.entries(groupedTech).map(([category, techs]) => {
              const config = getCategoryConfig(category);
              const Icon = config.icon;
              
              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 rounded ${config.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <h4 className="font-medium">{config.label}</h4>
                    <Badge variant="secondary" className="text-xs">{techs.length}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {techs.map((tech) => (
                      <Badge 
                        key={tech.id} 
                        variant="outline" 
                        className="px-3 py-1"
                        title={tech.source ? `Source: ${tech.source}` : undefined}
                      >
                        {tech.technology_name}
                        {tech.confidence_score && tech.confidence_score < 0.7 && (
                          <span className="ml-1 text-xs text-muted-foreground">?</span>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
